/**
 * 20261102 C3 진입점. 빌드·개발 설정과 관계없이 제품 통로가 있는 페이지에서 공통 owner를 동적으로 로드한다.
 * 구 frontend/구 앱/미지원 capability는 기존 경로로 즉시 또는 확인 후 돌아간다.
 * 소리 공유 on/off는 기존 frontend 전체 트랙 정책을 사용한다.
 * 20261021 C3 는 앱 native 송출이다: 웹은 미리보기·UI 만 하고 서버 송출 응답·보기·소리는 기존 Android(native) 경로로 보낸다.
 * @author hjkim.
 * @since 2026-10-20. (@modify 2026-10-20) (@modify 2026-10-21) (@modify 2026-10-26)
 */

let live = null;
let loading = null;
let disabled = false;
let nativeRecoveryBinding = null; // 20261026 R-187 C1/C2에서 동적 모듈을 열지 않는 지연 등록
const candidate = () => !disabled && !!window.PentalkRelay; // 20261102 제품 사용은 frontend와 앱의 자동 협상으로 결정한다.
const load = () => loading || (loading = import("./PentalkRelayLive").then(module => {
    live = module;
    if (nativeRecoveryBinding) live.bindNativeRecovery(...nativeRecoveryBinding);
    return module;
}));
const report = error => console.error("[RelayLive] 기존 경로 대체 없이 차단", error);

export const relayLive = {
    // 20261026 R-187 기존 native 복구 함수를 지연 연결한다. 등록만으로 모듈/장치/송출을 시작하지 않는다.
    bindNativeRecovery: (...binding) => {
        if (!candidate()) return;
        nativeRecoveryBinding = binding;
        if (live) live.bindNativeRecovery(...binding);
    },
    nativeStarted: () => { if (candidate() && live) live.nativeStarted(); },
    startVideo: ctx => {
        if (!candidate()) return false;
        load().then(async module => {
            if (module.isDisabled()) { disabled = true; ctx.legacy(); return; }
            await module.startVideo({ ...ctx, legacy: () => { disabled = true; ctx.legacy(); } });
            if (module.isDisabled()) disabled = true;
        }).catch(report);
        return true;
    },
    changeVideo: (info, lSeq, uSeq, button, clearMyCam) => {
        if (!candidate()) return false;
        load().then(module => module.changeVideo(info, lSeq, uSeq, button, clearMyCam)).catch(report);
        return true;
    },
    myCam: (info, initMyCam) => {
        if (!candidate()) return false;
        load().then(module => module.myCam({ info, initMyCam })).catch(report);
        return true;
    },
    setupAudio: async () => {
        if (!candidate()) return false;
        const module = await load();
        return module.setupAudio();
    },
    screenImage: info => {
        if (!candidate() || !live || !live.isActive()) return false;
        info.handsUpFunc(info.viewKind === "CAMERA" ? "viewKind_camera" : "viewKind_screen");
        // 20261021 앱 재송출 통지(pro-startedScreenShare)로 실행 중인 마이캠 표시가 꺼지지 않게 다시 표시
        if (info.viewKind === "SCREEN" && info.videoInputInfo.isMyCamRunning && info.myCamEl && info.myCamEl.srcObject) info.handsUpFunc("myCam_visible");
        return true;
    },
    nativeScreen: legacyScreen => legacyScreen || !!(candidate() && live && live.keepsAudio()), // 20261021 C3 는 CAMERA 여도 앱 native 송출 경로
    keepsAudio: () => candidate() && live && live.keepsAudio(),
    // 20261021 공용 파일은 조건/호출만. C3가 아닌 경우 기존 clone/constraints 경로 그대로 사용.
    sendVideoTrack: track => candidate() && live ? live.sendVideoTrack(track) : track.clone(),
    ownsVideo: track => !!(candidate() && live && live.ownsVideo(track)),
    quality: async (track, constraints, producer, reason) => candidate() && live ? live.videoQuality(track, constraints, producer, reason) : false,
    produced: async (producer, reason, transport) => { if (candidate() && live) await live.produced(producer, reason, transport); },
    traceAudio: (producer, reason, transport) => { if (candidate() && live) live.traceAudio(producer, reason, transport); },
    sound: (on, enable, disable) => candidate() && live && live.sound(on, enable, disable),
    close: async () => { if (live) await live.close(); },
    // 20261022 같은 페이지 끊김 복구(앱 송출 재시작) 때 서버 입장 기록 복원·수업 종료 확인 (R-173)
    rejoin: () => {
        if (!candidate() || !live || !live.isActive()) return false;
        const relay = live; // 20261022 R-178 세션·활성 상태를 await 뒤에도 다시 확인하도록 모듈째 넘긴다
        import("./PentalkRelayRejoin").then(module => module.rejoinLive(relay)).catch(report);
        return true;
    },
};

export const getRelayCameraStream = async facing => {
    if (!candidate()) return null;
    const module = await load();
    if (module.isDisabled()) return null;
    // C3 선택 후 실패/미반환은 throw. null로 gUM 우회를 열지 않는다(R-138).
    return module.cameraStream(facing);
};
export const stopRelayCamera = async () => { if (live) await live.releaseCamera(); };
